import {
  Configuration,
  MemberAuthApi,
  MemberPortalBillingAchHandlersApi,
  MemberPortalBillingHandlersApi,
  MemberPortalCommentHandlersApi,
  MemberPortalFileHandlersApi,
  MemberPortalNotificationHandlersApi,
  MemberPortalProfileHandlersApi,
  MemberPortalReservationHandlersApi,
  MemberPortalViolationHandlersApi,
} from './rust-api-client';
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import {
  clearAllTokens,
  getRefreshToken,
  getRememberMe,
  getToken,
  refreshAccessToken,
  setToken,
  tokenIsUnexpired,
} from '../utils/TokenUtils';

// Single base URL for the unified Rust backend. The variable keeps its
// `AUTH_SVC` name from the microservice era; there are no per-service
// suffixes any more, and appending one (`/api/vio`, `/api/notification`)
// is what left this app pointing at routes that no longer exist.
const API_BASE_URL = window._env_.REACT_APP_AUTH_SVC;

const apiConfig = new Configuration({
  basePath: API_BASE_URL,
});

// Authenticated requests. Carries the member access token and refreshes
// it when it has expired.
const apiAxiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Unauthenticated requests — login, password reset, activation. Kept
// separate so a stale token cannot ride along on a login attempt.
const authAxiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

const saveCurrentLocation = () => {
  // Only save if we're not already on the login page
  if (!window.location.pathname.includes('/login')) {
    const location = {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    };
    sessionStorage.setItem('returnTo', JSON.stringify(location));
  }
};

const requestInterceptor = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  let token = getToken();

  if (token) {
    const isUnexpired = await tokenIsUnexpired(token);

    if (!isUnexpired) {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        const newToken = await refreshAccessToken(refreshToken);
        if (newToken) {
          token = newToken;
          setToken(newToken, !!getRememberMe());
        } else {
          // Refresh failed, clear tokens and redirect to login
          clearAllTokens();
          saveCurrentLocation();
          window.location.href = '/login';
          throw new axios.Cancel(
            'Operation canceled due to token refresh failure.'
          );
        }
      } else {
        // No refresh token available, clear tokens and redirect to login
        clearAllTokens();
        saveCurrentLocation();
        window.location.href = '/login';
        throw new axios.Cancel(
          'Operation canceled due to missing refresh token.'
        );
      }
    }

    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  return config;
};

/**
 * The endpoints whose 401 says nothing about the session.
 *
 * `change-password` answers 401 when the **current password** field is
 * wrong. The token that carried the request is perfectly valid — the
 * failed credential check is the one in the form, not the one in the
 * header. Treating it like any other 401 would sign somebody out and
 * discard the form they were filling in because they mistyped a
 * password, which is the opposite of what the screen is for.
 *
 * Matched on the path suffix so it covers both realms:
 * `/api/v1/auth/change-password` and
 * `/api/v1/member-portal/auth/change-password`.
 */
const SESSION_INDEPENDENT_401 = /\/auth\/change-password$/;

// Common response interceptor to redirect 401 to login page
const responseInterceptor = (error: any) => {
  const requestUrl: string = error.config?.url ?? '';
  if (
    error.response &&
    error.response.status === 401 &&
    !SESSION_INDEPENDENT_401.test(requestUrl)
  ) {
    saveCurrentLocation();
    clearAllTokens();
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

apiAxiosInstance.interceptors.request.use(requestInterceptor, (error) =>
  Promise.reject(error)
);
apiAxiosInstance.interceptors.response.use(
  (response) => response,
  responseInterceptor
);

// ---------------------------------------------------------------------------
// Services
//
// These are the *only* APIs a member may call. The generated client also
// contains `HoaHandlersApi`, `ViolationHandlersApi`,
// `NotificationHandlersApi` and the rest of the management surface —
// those sit behind `AuthMiddleware`/`AuthUser` and answer **401** to a
// member token, because a homeowner has no role in the management
// hierarchy at all.
//
// Do not add them here. If a screen needs data with no member-portal
// endpoint behind it, the fix is an endpoint under
// `/api/v1/member-portal`, not an import from a management API — a
// mis-scoped call fails as a login loop, which is a long way from its
// cause.
// ---------------------------------------------------------------------------

/// Login, refresh, verify, password reset, activation. Unauthenticated.
export const memberAuthService = new MemberAuthApi(
  apiConfig,
  API_BASE_URL,
  authAxiosInstance
);

/**
 * The one member auth endpoint that has to be called *as somebody*.
 *
 * See the manager app's `authSessionService` for the full reasoning: logout
 * identifies the caller by their bearer token, so on the unauthenticated
 * instance it 401s and ends no session at all.
 */
export const memberAuthSessionService = new MemberAuthApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

/// The signed-in member and their homes. The only source of the member's
/// name, email and addresses — none of that is in the JWT.
export const memberProfileService = new MemberPortalProfileHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

/// Balance, statement and coupon for the member's own homes. Money
/// arrives as decimal strings — never parse them into a float.
export const memberBillingService = new MemberPortalBillingHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

/// Paying by bank account: the account on file, the authorisation, a
/// payment that the association settles. The one member write that
/// touches money — and it moves nothing until staff settle it.
export const memberAchService = new MemberPortalBillingAchHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

/// Violations cited against the member's homes. Read-only.
export const memberViolationService = new MemberPortalViolationHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

/// The member's own notifications, plus read/unread state.
export const memberNotificationService =
  new MemberPortalNotificationHandlersApi(
    apiConfig,
    API_BASE_URL,
    apiAxiosInstance
  );

export { authAxiosInstance, apiAxiosInstance };

/// Reading and replying to the shared thread on the member's own
/// violations. A member-realm API — `MemberPortalCommentHandlersApi`, not
/// the management `CommentHandlersApi`, which sits behind `AuthUser` and
/// would 401 — so it belongs here under the rule above.
///
/// The server decides the scope: a member is always `SharedOnly`, and an
/// internal comment 404s exactly as a nonexistent one does, so the id
/// space cannot be probed for the existence of the other thread.
export const memberCommentService = new MemberPortalCommentHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

/// Serving a comment's attachment inline, for display. Member-realm, and
/// authorized through the attachment graph rather than HOA membership — a
/// file attached to nothing 404s even inside the member's own HOA.
///
/// There is no upload counterpart: `files.user_id` is NOT NULL and a
/// homeowner has no `users` row, so attaching to one's own comment needs
/// a schema change and is #581.
export const memberFileService = new MemberPortalFileHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);

/// Amenities at the member's association, their availability, and the
/// member's own bookings (#643). The portal's first real create flow:
/// booking and cancelling are writes, so an impersonated session gets
/// **403** on them (never 401 — see `ImpersonationBanner`), and the
/// screens disable those buttons rather than letting it happen.
export const memberReservationService = new MemberPortalReservationHandlersApi(
  apiConfig,
  API_BASE_URL,
  apiAxiosInstance
);
